import { useEffect, useState } from 'react';
import { DEFAULT_CONTROL_HR_SUMMARY_MODAL_VALUE, useReportStore } from '@/entities/report';
import type { DailySummaryEventHrInfoProps } from './dailySummaryEventHrInfo';

/**
 * Backs the HR summary panel that steps through the events sharing the selected minute. Tracks a local index seeded from the incoming default, resolves the current event with its type and channel falling back to the AI channel, reports the mapped index back to the parent while the panel is active, and opens the set min and max HR modal on demand.
 *
 * @param props - The panel props with the events, their indexes, the active flag, the AI channel, the default index and the selected-index setter.
 * @returns An object carrying the current index, the events, the active flag, the resolved event with its type and channel, and the arrow and set-HR handlers.
 */
const useDailySummaryEventHrInfo = ({
  eventIndexes,
  events,
  isActive,
  aiChannel,
  setSelectedEventIndex,
  defaultIndex,
}: Pick<
  DailySummaryEventHrInfoProps,
  'eventIndexes' | 'events' | 'isActive' | 'aiChannel' | 'setSelectedEventIndex' | 'defaultIndex'
>) => {
  const [eventIndex, setEventIndex] = useState(defaultIndex);

  const setControlModalHrSummary = useReportStore((s) => s.actions.setControlHrSummaryModal);

  const event = events[eventIndex] || events[0];

  //* if not event type => minute info
  const eventType = event.type || event?.oldType;
  const channel = event?.channel || aiChannel;

  //* if is select event 1, click arrow left will select event 3, arrow right will select event 2
  /**
   * Moves the selection to the previous event of the minute shown in the HR info panel. Decrements the index and wraps around to the last event when it drops below zero.
   *
   * @returns Nothing.
   */
  const onClickLeft = () => {
    const newIndex = eventIndex - 1;
    setEventIndex(newIndex < 0 ? (events?.length ?? 0) - 1 : newIndex);
  };

  /**
   * Moves the selection to the next event of the minute shown in the HR info panel. Increments the index and wraps back to the first event once it passes the last one.
   *
   * @returns Nothing.
   */
  const onClickRight = () => {
    const newIndex = eventIndex + 1;
    setEventIndex(newIndex > (events?.length ?? 0) - 1 ? 0 : newIndex);
  };

  /**
   * Opens the dialog for overriding the day's maximum or minimum heart rate. Resets the HR summary modal control to its default values with the set min and max HR flag raised.
   *
   * @returns Nothing.
   */
  const onClickSetMaxMinHr = () => {
    setControlModalHrSummary({
      ...DEFAULT_CONTROL_HR_SUMMARY_MODAL_VALUE,
      isShowModalSetMaxMinHr: true,
    });
  };

  useEffect(() => {
    setEventIndex(defaultIndex);
  }, [defaultIndex]);

  useEffect(() => {
    if (isActive && events?.length > 1) {
      setSelectedEventIndex(eventIndexes[eventIndex] || 0);
    }
  }, [eventIndex, isActive]);

  return {
    eventIndex,
    events,
    isActive,
    event,
    eventType,
    channel,
    onClickLeft,
    onClickRight,
    onClickSetMaxMinHr,
  };
};

export default useDailySummaryEventHrInfo;
